// BLOQ Talk — Quantum Shield client (PQC Path 1). PORTED VERBATIM from the mobile
// `frontend/src/utils/pqc.ts` (fetched from /api/admin/docs/cl-pqc). The only web
// adaptations allowed by the mobile team: dropped `import "./cryptoPolyfill"` (browsers
// have crypto.getRandomValues) and repointed the `api` import. Crypto is byte-identical.
import { ml_kem768 } from "@noble/post-quantum/ml-kem.js";
import { x25519 } from "@noble/curves/ed25519.js";
import { hkdf } from "@noble/hashes/hkdf.js";
import { sha256 } from "@noble/hashes/sha2.js";
import { hmac } from "@noble/hashes/hmac.js";
import { api } from "./clApi";

let status = { state: "idle" };
let inflight = null;
const listeners = new Set();

function setStatus(next) {
  status = next;
  listeners.forEach((fn) => fn(status));
}

export function getQuantumStatus() {
  return status;
}

export function subscribeQuantum(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function resetQuantumSession() {
  inflight = null;
  setStatus({ state: "idle" });
}

// ── byte helpers (no atob/btoa dependency — Hermes-safe) ──
const B64_ABC = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";

export function b64encode(bytes) {
  let out = "";
  for (let i = 0; i < bytes.length; i += 3) {
    const b0 = bytes[i];
    const b1 = i + 1 < bytes.length ? bytes[i + 1] : 0;
    const b2 = i + 2 < bytes.length ? bytes[i + 2] : 0;
    out += B64_ABC[b0 >> 2] + B64_ABC[((b0 & 3) << 4) | (b1 >> 4)];
    out += i + 1 < bytes.length ? B64_ABC[((b1 & 15) << 2) | (b2 >> 6)] : "=";
    out += i + 2 < bytes.length ? B64_ABC[b2 & 63] : "=";
  }
  return out;
}

export function b64decode(s) {
  const clean = s.replace(/=+$/, "");
  const out = [];
  let buffer = 0;
  let bits = 0;
  for (const ch of clean) {
    buffer = (buffer << 6) | B64_ABC.indexOf(ch);
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      out.push((buffer >> bits) & 0xff);
    }
  }
  return Uint8Array.from(out);
}

export function toHex(bytes) {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

export function concat(a, b) {
  const out = new Uint8Array(a.length + b.length);
  out.set(a);
  out.set(b, a.length);
  return out;
}

export function utf8(s) {
  return new TextEncoder().encode(s);
}

async function performHandshake(userId) {
  const keys = await api("/pqc/server-keys");
  // 1) post-quantum KEM: encapsulate against the server vault's ML-KEM-768 key
  const { cipherText, sharedSecret: kemSS } = ml_kem768.encapsulate(b64decode(keys.kem_pub));
  // 2) classical layer: ephemeral X25519 with the vault's curve key
  const xPriv = globalThis.crypto.getRandomValues(new Uint8Array(32));
  const xPub = x25519.getPublicKey(xPriv);
  const xSS = x25519.getSharedSecret(xPriv, b64decode(keys.x25519_pub));
  // 3) server derives the same key and proves it via HMAC confirmation
  const resp = await api("/pqc/handshake", {
    method: "POST",
    body: { kem_ct: b64encode(cipherText), client_x_pub: b64encode(xPub) },
  });
  const sessionKey = hkdf(sha256, concat(kemSS, xSS), utf8("bloq-session-v1"), utf8(userId), 32);
  const expected = toHex(hmac(sha256, sessionKey, utf8(`bloq-confirm:${resp.session_id}`)));
  if (expected !== resp.confirm) {
    return { state: "unavailable" };
  }
  return { state: "active", fingerprint: resp.fingerprint, alg: resp.alg, sessionId: resp.session_id };
}

export async function ensureQuantumSession(userId, force = false) {
  if (!force && status.state === "active") return status;
  if (inflight) return inflight;
  inflight = (async () => {
    setStatus({ state: "establishing" });
    try {
      setStatus(await performHandshake(userId));
    } catch {
      setStatus({ state: "unavailable" });
    } finally {
      inflight = null;
    }
    return status;
  })();
  return inflight;
}
